import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { userStore, bookStore } from '../../store';
import { hitApi } from '../../database/services/ApiHelper';
import { API } from '../../database/apis';
import { ArrowLeft } from 'lucide-react';

// PopularAll — "See all" destination from the Discover landing's
// "Popular this week / month / all" carousel. Same backend endpoint,
// rendered as a vertical list rather than a carousel, no item cap.
//
// Accepts ?window=week|month|all in the URL so the user lands on the
// same window they were viewing in the carousel.

type PopularWindow = 'week' | 'month' | 'all';
type BookResult = {
  id: string;
  title: string;
  authorName: string;
  coverUrl?: string | null;
  averageRating?: number | null;
  reviewCount: number;
  category?: string;
};

const BRAND = '#5B47E0';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';
const STAR_YELLOW = '#FFB800';

const PopularAllScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialWindow = (params.get('window') as PopularWindow) || 'week';

  const [popular, setPopular] = useState<BookResult[]>([]);
  const [popularWindow, setPopularWindow] = useState<PopularWindow>(initialWindow);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void fetchPopular(popularWindow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popularWindow]);

  async function fetchPopular(win: PopularWindow) {
    setLoading(true);
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `${API.POPULAR}?window=${win}&limit=30`,
        method: 'GET',
        token,
      });
      setPopular(Array.isArray(res?.books) ? res.books : []);
    } catch (e) {
      // hitApi throws { status, message, data } shapes (not Error
      // instances), so we swallow them locally — the screen falls back
      // to its empty-state copy. Without this the promise rejection
      // surfaces in the dev-server overlay as "[object Object]".
      console.log('[PopularAll] fetch failed', e);
      setPopular([]);
    } finally {
      setLoading(false);
    }
  }

  function openBook(bookId: string) {
    const full = userStore.adverBooks.find((b: any) => b._id === bookId);
    if (full) {
      bookStore.setSelectedBook(toJS(full));
    } else {
      const row = popular.find((b) => b.id === bookId);
      if (row) {
        bookStore.setSelectedBook({
          _id: row.id,
          title: row.title,
          authorName: row.authorName,
          category: row.category || '',
          coverImage: row.coverUrl || '',
          description: '',
          ad_slots: [],
        } as any);
      }
    }
    navigate('/book');
  }

  return (
    <div className="h-full flex flex-col" style={{ background: '#FFFFFF' }}>
      {/* Top bar */}
      <div className="safe-area-top" style={{ background: '#FFFFFF' }}>
        <div className="px-5 pt-4 pb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ background: SURFACE }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: TEXT_PRIMARY }} />
          </button>
          <h1
            className="text-[22px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY }}
          >
            {popularWindow === 'week' ? 'Popular this week'
              : popularWindow === 'month' ? 'Popular this month'
              : 'Popular of all time'}
          </h1>
        </div>

        {/* Window toggle */}
        <div className="px-5 pb-3 flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {(['week', 'month', 'all'] as PopularWindow[]).map((w) => (
            <button
              key={w}
              onClick={() => setPopularWindow(w)}
              className="flex-shrink-0 h-9 px-4 rounded-full text-[13px] font-semibold transition-colors"
              style={{
                background: popularWindow === w ? BRAND : '#FFFFFF',
                color: popularWindow === w ? '#FFFFFF' : TEXT_MUTED,
                border: popularWindow === w ? 'none' : '1px solid #E2E8F0',
              }}
            >
              {w === 'week' ? 'This week' : w === 'month' ? 'This month' : 'All time'}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pb-6">
        {loading && popular.length === 0 ? (
          <div className="px-5 pt-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-3"
                style={{ borderBottom: '1px solid #F1F5F9' }}
              >
                <div className="w-14 h-[72px] rounded-xl" style={{ background: SURFACE }} />
                <div className="flex-1">
                  <div className="h-3 rounded w-3/5 mb-2" style={{ background: SURFACE }} />
                  <div className="h-3 rounded w-2/5" style={{ background: SURFACE }} />
                </div>
              </div>
            ))}
          </div>
        ) : popular.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <p className="text-[16px] font-semibold" style={{ color: TEXT_PRIMARY }}>
              No popular books yet
            </p>
            <p className="text-[13px] mt-1" style={{ color: TEXT_MUTED }}>
              Check back once more readers leave reviews.
            </p>
          </div>
        ) : (
          <div className="px-5">
            {popular.map((b, i) => (
              <button
                key={b.id}
                onClick={() => openBook(b.id)}
                className="w-full flex items-center gap-3 py-3 text-left transition-colors active:bg-[#F8FAFC]"
                style={{ borderBottom: '1px solid #F1F5F9' }}
              >
                <span
                  className="w-7 text-[15px] font-bold flex-shrink-0 text-center"
                  style={{ color: TEXT_MUTED }}
                >
                  {i + 1}
                </span>
                <div
                  className="w-14 h-[72px] rounded-xl flex-shrink-0 overflow-hidden"
                  style={{ background: '#E2E8F0' }}
                >
                  {b.coverUrl && (
                    <img src={b.coverUrl} alt={b.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold leading-tight truncate" style={{ color: TEXT_PRIMARY }}>
                    {b.title}
                  </p>
                  <p className="text-[13px] mt-0.5 truncate" style={{ color: TEXT_MUTED }}>
                    {b.authorName}
                  </p>
                  {typeof b.averageRating === 'number' && b.averageRating > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[12px]" style={{ color: STAR_YELLOW }}>★</span>
                      <span className="text-[12px] font-semibold" style={{ color: TEXT_PRIMARY }}>
                        {b.averageRating.toFixed(1)}
                      </span>
                      {b.reviewCount > 0 && (
                        <span className="text-[12px]" style={{ color: TEXT_MUTED }}>
                          ({formatCount(b.reviewCount)})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export default PopularAllScreen;
